import { Component, computed, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import gamesList from '../../../../../public/games/games.json';

/**
 * Interface matching the C# GameSettings backend model.
 * Used as the gameMetaData payload sent via chatService.launch().
 */
export interface GameSettingsData {
  game?: string;
  map?: string;
  mod?: string;
  maxPlayerCount?: number;
  roundTimer?: number;
  roundTimeLimit?: number;
  maps?: string[];
  roomGuid?: string;
  mode?: string;
  winScore?: number;
  loseScore?: number;
  timer?: number;
  isQuickPlay?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-game-settings-modal',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './game-settings-modal.html',
  styleUrl: './game-settings-modal.scss',
})
export class GameSettingsModal implements OnInit {
  @Input()
  public gameId: number = 0;

  @Input()
  public gameTitle: string = 'Game';

  @Input()
  public set roomGuid(value: string) {
    this._roomGuid = value || '';
    if (this.settingsForm) {
      this.settingsForm.patchValue({
        roomGuid: this._roomGuid,
      });
    }
  }
  public get roomGuid(): string {
    return this._roomGuid;
  }
  private _roomGuid: string = '';

  @Output()
  public settingsSaved = new EventEmitter<GameSettingsData>();

  /** The settings schema from games.json for this game. */
  public settingsSchema = signal<Record<string, any> | null>(null);

  /** Tracks which maps are selected in the map pool. */
  public selectedMaps = signal<string[]>([]);

  /** Dynamically available map options based on the currently selected mod. */
  public availableMapOptions = signal<string[]>([]);

  /** Reactive form for settings fields. */
  public settingsForm = new FormGroup({
    game: new FormControl(''),
    map: new FormControl(''),
    mod: new FormControl(''),
    maxPlayerCount: new FormControl(16),
    roundTimer: new FormControl(15),
    roundTimeLimit: new FormControl(15),
    roomGuid: new FormControl(''),
    mode: new FormControl('Team'),
    winScore: new FormControl(250),
    loseScore: new FormControl(-200),
    timer: new FormControl(30),
    isQuickPlay: new FormControl(false),
  });

  ngOnInit(): void {
    this.loadSchema();

    // Listen to mod changes to dynamically update map choices
    this.settingsForm.controls.mod.valueChanges.subscribe(newMod => {
      if (newMod !== null && newMod !== undefined) {
        this.onModChange(newMod);
      }
    });
  }

  /*
   * Load the settings schema from games.json for the given gameId and
   * initialize form defaults.
   */
  private loadSchema(): void {
    let game = (gamesList as any[]).find(
      (g: any) => g.gameId === this.gameId || g.gameId === Number(this.gameId)
    );
    if (!game || !game.settings) {
      game = (gamesList as any[]).find((g: any) => g.gameId === 1);
    }

    if (this.roomGuid) {
      this.settingsForm.controls.roomGuid.setValue(this.roomGuid);
    }

    if (!game?.settings) {
      this.settingsSchema.set(null);
      return;
    }

    const schema = game.settings as Record<string, any>;
    this.settingsSchema.set(schema);

    // Set form defaults from schema.
    if (schema['game']) {
      this.settingsForm.controls.game.setValue(schema['game'].default ?? '');
    }
    const defaultMod = schema['mod']?.default ?? '';
    if (schema['mod']) {
      this.settingsForm.controls.mod.setValue(defaultMod);
    }
    if (schema['maxPlayerCount']) {
      this.settingsForm.controls.maxPlayerCount.setValue(schema['maxPlayerCount'].default ?? 16);
    }
    if (schema['roundTimer']) {
      this.settingsForm.controls.roundTimer.setValue(schema['roundTimer'].default ?? 15);
    }
    if (schema['roundTimeLimit']) {
      this.settingsForm.controls.roundTimeLimit.setValue(schema['roundTimeLimit'].default ?? 15);
    }
    if (schema['mode']) {
      this.settingsForm.controls.mode.setValue(schema['mode'].default ?? 'Team');
    }
    if (schema['winScore']) {
      this.settingsForm.controls.winScore.setValue(schema['winScore'].default ?? 250);
    }
    if (schema['loseScore']) {
      this.settingsForm.controls.loseScore.setValue(schema['loseScore'].default ?? -200);
    }
    if (schema['timer']) {
      this.settingsForm.controls.timer.setValue(schema['timer'].default ?? 30);
    }
    if (schema['isQuickPlay']) {
      this.settingsForm.controls.isQuickPlay.setValue(Boolean(schema['isQuickPlay'].default));
    }

    // Populate available maps based on default mod selection
    this.onModChange(defaultMod);

    // Override map pool defaults if specified in schema
    if (schema['maps'] && schema['maps'].default) {
      const validDefaults = schema['maps'].default.filter((m: string) =>
        this.availableMapOptions().includes(m)
      );
      if (validDefaults.length > 0) {
        this.selectedMaps.set(validDefaults);
      }
    }
  }

  /*
   * Helper to normalize select field options into objects containing value and label.
   */
  public getSelectOptions(key: string): Array<{ value: any; label: string }> {
    const schema = this.settingsSchema();
    if (!schema || !schema[key] || !schema[key].options) return [];

    return schema[key].options.map((opt: any) => {
      if (typeof opt === 'object' && opt !== null && 'value' in opt) {
        return { value: opt.value, label: opt.label || String(opt.value) };
      }
      return { value: opt, label: String(opt) };
    });
  }

  /*
   * Updates map options and defaults whenever the selected mod changes.
   * Sets starting map to the first available map for the chosen mod.
   */
  public onModChange(newMod: string): void {
    const schema = this.settingsSchema();
    if (!schema) return;

    const mapSchema = schema['map'];
    let mapsForMod: string[] = [];

    if (mapSchema?.optionsByMod && mapSchema.optionsByMod[newMod]) {
      mapsForMod = mapSchema.optionsByMod[newMod];
    } else if (mapSchema?.options) {
      mapsForMod = mapSchema.options;
    }

    this.availableMapOptions.set(mapsForMod);

    // Set starting map to the first map of the newly selected mod
    if (mapsForMod.length > 0) {
      this.settingsForm.controls.map.setValue(mapsForMod[0]);
      this.selectedMaps.set([mapsForMod[0]]);
    } else {
      this.settingsForm.controls.map.setValue('');
      this.selectedMaps.set([]);
    }
  }

  /*
   * Toggle a map in the map pool selection.
   */
  public toggleMap(mapName: string): void {
    const current = this.selectedMaps();
    if (current.includes(mapName)) {
      this.selectedMaps.set(current.filter(m => m !== mapName));
    } else {
      this.selectedMaps.set([...current, mapName]);
    }
  }

  /*
   * Check if a map is selected in the pool.
   */
  public isMapSelected(mapName: string): boolean {
    return this.selectedMaps().includes(mapName);
  }

  /*
   * Build the GameSettingsData payload from form values and emit it.
   */
  public saveSettings(): void {
    const settings = this.getSettings();
    console.log('Game settings saved:', settings);
    this.settingsSaved.emit(settings);
  }

  /*
   * Build and return the current GameSettingsData without emitting.
   * Used by the parent to read settings at launch time.
   */
  public getSettings(): GameSettingsData {
    const formVal = this.settingsForm.value;

    return {
      game: formVal.game ?? '',
      map: formVal.map ?? '',
      mod: formVal.mod ?? '',
      maxPlayerCount: Number(formVal.maxPlayerCount) || 16,
      roundTimer: Number(formVal.roundTimer) || 15,
      roundTimeLimit: Number(formVal.roundTimeLimit) || 15,
      maps: [...this.selectedMaps()],
      roomGuid: formVal.roomGuid || this.roomGuid || '',
      mode: formVal.mode ?? 'Team',
      winScore: formVal.winScore !== null && formVal.winScore !== undefined ? Number(formVal.winScore) : 250,
      loseScore: formVal.loseScore !== null && formVal.loseScore !== undefined ? Number(formVal.loseScore) : -200,
      timer: formVal.timer !== null && formVal.timer !== undefined ? Number(formVal.timer) : 30,
      isQuickPlay: Boolean(formVal.isQuickPlay),
    };
  }
}

